import React from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { translator } from 'mage-i18n';

import {
  AlertBlock,
  ButtonTertiary,
  ColoredIllustrationErrorExplodingBalloon,
  SpacerVertical,
  Typography,
} from '@klarna/bubble-ui';
import { ContainerTemplate } from '@/components';
import { getDecodedWidgetToken } from '@/utils/widget';
import { IS_MERCHANT_VERIFICATION_REVIEW_INFO, IS_MERCHANT_VERIFICATION_WIDGET } from '@/constants';
import { parseReturnUrl } from './MerchantVerification.utils';

interface VerificationInfoProps {
  t: TranslatorFunction;
  onNext?: () => void;
}

export const VerificationInfo = ({ t, onNext }: VerificationInfoProps): React.JSX.Element => {
  const { search } = useLocation();
  const tPrefix = 'products-fe.containers.merchant_verification_info';
  const queryParams = new URLSearchParams(search);
  const decodedWidgetToken = getDecodedWidgetToken();
  const returnUrl = queryParams.get('return_url') || decodedWidgetToken?.returnUrl;
  const storeName = decodedWidgetToken?.storeName;

  const subtitleMessage =
    IS_MERCHANT_VERIFICATION_REVIEW_INFO && !IS_MERCHANT_VERIFICATION_WIDGET
      ? t(`${tPrefix}.subtitle_in_review`)
      : t(`${tPrefix}.subtitle_start_review`, { storeName });
  const alertBlockMessage =
    IS_MERCHANT_VERIFICATION_REVIEW_INFO && !IS_MERCHANT_VERIFICATION_WIDGET
      ? t(`${tPrefix}.alert.message_in_review`)
      : t(`${tPrefix}.alert.message_start_review`);

  const onButtonClick = () => {
    if (returnUrl) {
      const parsedUrl = parseReturnUrl(returnUrl);
      if (parsedUrl) {
        window.location.href = parsedUrl;
      }
    } else {
      window.close();
    }
  };

  return (
    <ContainerTemplate showRecaptchaFooter>
      <ColoredIllustrationErrorExplodingBalloon />
      <SpacerVertical spaceToken="space/400" />
      <Typography textToken="text-style/headings/titles/bold/huge">
        {t(`${tPrefix}.title`)}
      </Typography>
      <SpacerVertical spaceToken="space/400" />
      <Typography textToken="text-style/text/labels/tall/regular">{subtitleMessage}</Typography>
      <SpacerVertical spaceToken="space/400" />
      <AlertBlock
        content={[alertBlockMessage]}
        linkLabel={onNext && t(`${tPrefix}.alert.verification_button`)}
        onLinkClick={onNext && (() => onNext())}
      />
      <SpacerVertical spaceToken="space/800" />
      <ButtonTertiary onClick={() => onButtonClick()} onPress={() => onButtonClick()}>
        {t(`${tPrefix}.button_close`)}
      </ButtonTertiary>
    </ContainerTemplate>
  );
};

VerificationInfo.propTypes = {
  t: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default connect(state => ({
  t: translator(state),
}))(VerificationInfo);
